import { Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { FcCancel } from 'react-icons/fc';
import { GrEdit } from 'react-icons/gr';
import { InfinitySpin } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { api_url } from '../../../config';
import { hasPermission } from '../../../Redux/permissionSlice';
import { selectRoles } from '../../../Redux/AuthSlice';
import api from '../../../utils/api';
import { formatDate } from '../../../utils/dateUtils';
import {
    validateEmail,
    validateGhanaPhone,
    validateGhanaPost,
    validateRequired,
    validateName,
    validateAddress,
    validateCity,
    validateTown
} from '../../../utils/validation';

const SupplierDetail = ({ supplier, fetchSuppliers }) => {
    const [loading, setLoading] = useState(false)
    const [name, setName] = useState(supplier?.name)
    const [city, setCity] = useState(supplier?.city)
    const [town, setTown] = useState(supplier?.town)
    const [address, setAddress] = useState(supplier?.address)
    const [email, setEmail] = useState(supplier?.email)
    const [contact, setContact] = useState(supplier?.phone)
    const [status, setStatus] = useState('')
    const [supplier_status, setSupplier_status] = useState('')
    const [error, setError] = useState('')
    const [ghana_post, setGhana_post] = useState(supplier?.ghana_post)
    const [editMode, setEditMode] = useState(false);

    const checker = supplier?.status === 'active'
    const { id, branch_id, token } = useSelector((state) => state.auth);
    const selectedRoles = useSelector(selectRoles);
    const roles = Array.isArray(selectedRoles) ? selectedRoles : selectedRoles.split(',').map(role => role.trim());

    const handleEditMode = () => {
        setEditMode(!editMode);
    };

    useEffect(() => {
        setName(supplier?.name || '');
        setContact(supplier?.phone || '');
        setEmail(supplier?.email || '');
        setCity(supplier?.city || '');
        setTown(supplier?.town || '');
        setAddress(supplier?.address || '');
        setGhana_post(supplier?.ghana_post || '');
        setStatus(supplier?.status || '');
    }, [supplier, editMode]);

    const handleUserStatus = (checked) => {
        const status = checked ? 'Active' : 'Inactive';
        setStatus(status);
    }

    const editSupplier = async () => {
        setLoading(true);

        try {
            const updatedSupplier = {
                name,
                email,
                city,
                town,
                address,
                phone: contact,
                ghana_post,
                status,
            };

            // Validate all required fields
            const requiredFields = {
                name: 'Name',
                city: 'City',
                email: 'Email',
                phone: 'Phone',
                address: 'Address',
                town: 'Town'
            };

            const missingFields = Object.entries(requiredFields)
                .filter(([key]) => !validateRequired(updatedSupplier[key]))
                .map(([_, label]) => label);

            if (missingFields.length > 0) {
                setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate name
            if (!validateName(name)) {
                setError('Name should only contain letters, spaces, and basic punctuation');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate email
            if (!validateEmail(email)) {
                setError('Please enter a valid email address');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate phone
            if (!validateGhanaPhone(contact)) {
                setError('Please enter a valid Ghana phone number (e.g., 0200000000 or +233200000000)');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate city
            if (!validateCity(city)) {
                setError('City should only contain letters, spaces, and basic punctuation');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate town
            if (!validateTown(town)) {
                setError('Town should only contain letters, spaces, and basic punctuation');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate address
            if (!validateAddress(address)) {
                setError('Address contains invalid characters');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            // Validate Ghana Post (optional)
            if (ghana_post && !validateGhanaPost(ghana_post)) {
                setError('Ghana Post should be in format: GM-000-0000');
                setTimeout(() => {
                    setError('');
                }, 6000);
                setLoading(false);
                return;
            }

            const response = await api.put(
                `${api_url}/api/v1/wholesaler/user/${id}/branch/${branch_id}/supplier/${supplier.id}`,
                updatedSupplier,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSupplier_status('Supplier updated successfully');
            setError('');
            fetchSuppliers(1, 50, '')
            setTimeout(() => {
                setSupplier_status('');
                setEditMode(false);
            }, 3000);

        } catch (error) {
            if (error.response && error.response.data) {
                setError(error.response.data);
            } else {
                setError('An error occurred while updating the supplier');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className='relative'>
                {loading && (
                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
                        <InfinitySpin color="#008DDA" loading={loading} size={40} />
                    </div>
                )}
                <div>
                    {supplier?.status === "archived" ? null : <div className='flex flex-col justify-between mb-6 sm:flex-row'>
                        <div className='flex items-center gap-4 my-1 '>
                            {hasPermission(roles, ['supplierWriter', 'supplierAdmin']) &&
                                <button onClick={handleEditMode} className={`flex items-center gap-1 p-2 px-4 font-semibold text-center  ${!editMode ? 'hover:border-blue-400' : 'hover:border-red-600'} text-blue-800 bg-white border rounded-md font-lufga `}>   {!editMode ? <><GrEdit /><span className='text-xs '>Edit</span></> : <><FcCancel color='blue' /><span className='text-xs text-red-600'>Cancel</span></>} </button>
                            }
                        </div>
                        <div className=' absolute flex items-center gap-3 right-1 sm:right-[2rem] lg:right-[3rem] lg:top-0 top-4 '>
                            <Switch
                                style={{
                                    borderRadius: '9999px',
                                }}
                                defaultChecked={checker}
                                onChange={handleUserStatus}
                                disabled={!editMode}
                            />
                            <label className={`text-sm font-lufga hidden sm:block ${status === 'Active' ? 'text-blue-600' : 'text-gray-500'}`}>
                                {status || supplier?.status}
                            </label>
                        </div>
                    </div>}
                    <form className="grid gap-4 mb-6 md:grid-cols-2">
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga" htmlFor="name">Name<span className='font-serif text-red-400'>*</span></label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={editMode ? name : supplier?.name}
                                onChange={(e) => { setName(e.target.value) }}
                                placeholder="Enter supplier's name"
                                required
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga">Email<span className='font-serif text-red-400'>*</span></label>
                            <input
                                type="text"
                                value={editMode ? email : supplier?.email}
                                onChange={(e) => { setEmail(e.target.value) }}
                                placeholder="Enter supplier's email"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                                required
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga">City<span className='font-serif text-red-400'>*</span></label>
                            <input
                                type="text"
                                value={editMode ? city : supplier?.city}
                                onChange={(e) => { setCity(e.target.value) }}
                                placeholder="Enter supplier's city"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                                required
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga" htmlFor="address">Town<span className='font-serif text-red-400'>*</span></label>
                            <input
                                type="text"
                                value={editMode ? town : supplier?.town}
                                onChange={(e) => { setTown(e.target.value) }}
                                placeholder="Enter supplier's town"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                                required
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga">Address<span className='font-serif text-red-400'>*</span></label>
                            <input
                                type="text"
                                value={editMode ? address : supplier?.address}
                                onChange={(e) => { setAddress(e.target.value) }}
                                placeholder="Enter supplier's address"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                                required
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga" htmlFor="address">Ghana Post</label>
                            <input
                                type="text"
                                value={editMode ? ghana_post : supplier?.ghana_post}
                                onChange={(e) => { setGhana_post(e.target.value) }}
                                placeholder="Enter Ghana Post code (GM-000-0000)"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga" htmlFor="phone">Phone Number<span className='font-serif text-red-400'>*</span></label>
                            <input
                                type="text"
                                id="phone"
                                name="phone"
                                value={editMode ? contact : supplier?.phone}
                                onChange={(e) => { setContact(e.target.value) }}
                                placeholder="Enter phone number"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={!editMode}
                                required
                            />
                        </div>
                        <div className='flex flex-col gap-3'>
                            <label className="flex items-center gap-1 text-sm font-semibold font-lufga" htmlFor="phone">Date Created</label>
                            <input
                                type="text"
                                id="phone"
                                name="phone"
                                value={formatDate(new Date(supplier?.created_at).toLocaleDateString())}
                                placeholder="Enter phone number"
                                className={`px-3 py-2  rounded-md focus:outline-none focus:border-blue-500 ${editMode ? 'border border-gray-300' : ''}`}
                                disabled={true}
                            />
                        </div>
                    </form>

                    <div className='flex items-center justify-between'>
                        {editMode && <button onClick={editSupplier} className="px-4 py-2 ml-auto text-white transition duration-300 bg-blue-500 rounded-md shadow-md font-lufga hover:bg-blue-600">Update</button>}
                        {supplier_status ? <div>
                            <p className="px-2 py-1 text-xs text-center bg-green-300 rounded-md shadow-md font-lufga ">{supplier_status}</p>
                        </div> : error ? <div>
                            <p className="px-2 py-1 text-sm text-center bg-red-300 rounded-md shadow-md font-lufga ">
                                {typeof error === 'string' ? error : (
                                    Object.keys(error).map((key) => (
                                        Array.isArray(error[key]) ? error[key][0] : error[key]
                                    ))
                                )}
                            </p>
                        </div> : null}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SupplierDetail;
